import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RealtimeConnection, RealtimeEnvelope } from '@zayan-erp/realtime';
import { ToastrService } from 'ngx-toastr';
import { BulkDownloadEvents, BulkDownloadStartedPayload } from '../models/bulk-download-events';

/**
 * Raises bulk-download toasts from anywhere in the app.
 *
 * Root-provided and subscribed in its constructor, so the toast no longer depends on the
 * bulk-download panel being on screen — the user gets it whatever page they are on. Instantiated
 * once at bootstrap (see app.config.ts); nothing else should inject it.
 *
 * The panel keeps its own subscription for the job list it renders. This service only toasts, so
 * the two do not double up.
 *
 * The wording lives here and only here. The server publishes the facts; presentation is the
 * client's, so copy changes ship with the frontend and need no backend deploy.
 */
@Injectable({ providedIn: 'root' })
export class BulkDownloadNotificationsService {
  #realtime = inject(RealtimeConnection);
  #toaster = inject(ToastrService);

  constructor() {
    this.#realtime.envelopes$.pipe(takeUntilDestroyed()).subscribe(envelope => this.#onEnvelope(envelope));
  }

  #onEnvelope(envelope: RealtimeEnvelope): void {
    switch (envelope.type) {
      case BulkDownloadEvents.started:
        return this.#onStarted(envelope.payload as BulkDownloadStartedPayload | undefined);
      case BulkDownloadEvents.ready:
        return this.#onReady();
      case BulkDownloadEvents.failed:
        return this.#onFailed();
      default:
        return;
    }
  }

  #onStarted(payload: BulkDownloadStartedPayload | undefined): void {
    const fileCount = payload?.fileCount ?? 0;
    const files = fileCount === 1 ? '1 file' : `${fileCount} files`;

    this.#toaster.info(`Preparing ${files} for download.`, 'Bulk download started');
  }

  #onReady(): void {
    this.#toaster.success('Your bulk download is ready to download.', 'Your ZIP is ready');
  }

  #onFailed(): void {
    this.#toaster.error("We couldn't build your ZIP. Please try again.", 'Bulk download failed');
  }
}
